import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { MatchingRound, MatchingRoundId } from '../api/matching-rounds';

export interface MatchingRoundsContextProps {
  resetKey: number;
  matchingRounds: MatchingRound[];
  selectedRoundIds: MatchingRoundId[];
  setMatchingRounds: Dispatch<SetStateAction<MatchingRound[]>>;
  setSelectedRoundIds: Dispatch<SetStateAction<MatchingRoundId[]>>;
  triggerReload: () => void;
}

interface MatchingRoundsProviderProps {
  children: ReactNode;
}

const MatchingRoundsContext = createContext<MatchingRoundsContextProps>({
  resetKey: 0,
  matchingRounds: [],
  selectedRoundIds: [],
  setMatchingRounds: () => {},
  setSelectedRoundIds: () => {},
  triggerReload: () => {},
});

export const MatchingRoundsProvider = ({
  children,
}: MatchingRoundsProviderProps) => {
  const [matchingRounds, setMatchingRounds] = useState<MatchingRound[]>([]);
  const [selectedRoundIds, setSelectedRoundIds] = useState<MatchingRoundId[]>(
    [],
  );
  const [resetKey, setResetKey] = useState(0);

  const triggerReload = useCallback(() => setResetKey((prev) => prev + 1), []);

  const contextValue = useMemo<MatchingRoundsContextProps>(
    () => ({
      resetKey,
      matchingRounds,
      selectedRoundIds,
      setMatchingRounds,
      setSelectedRoundIds,
      triggerReload,
    }),
    [resetKey, matchingRounds, selectedRoundIds, triggerReload],
  );

  return (
    <MatchingRoundsContext.Provider value={contextValue}>
      {children}
    </MatchingRoundsContext.Provider>
  );
};

export const useMatchingRoundsContext = () => {
  const context = useContext(MatchingRoundsContext);
  if (!context) {
    throw new Error(
      'useMatchingRoundsContext must be used within a MatchingRoundsProvider',
    );
  }
  return context;
};
